import {useState} from "react";
import {useMutation} from "@tanstack/react-query";
import {useApiRequest} from "../../../../../common/hooks/useApiRequest";
import {useSnackbar} from "../../../../../common/context/SnackbarContext";
import {ApiError} from "../../../../../common/types/requests";
import {LookupRequest, LookupResult} from "../types/lookup_types";

export interface UseLemmaLookupArgs {
    language: string;
    onLookedUp?: (lemma: string) => void;
}

export const useLemmaLookup = ({language, onLookedUp}: UseLemmaLookupArgs) => {

    const {postRequest} = useApiRequest();
    const {showSnackbar} = useSnackbar();

    const [lemma, setLemmaState] = useState<string>("");
    const [searchedLemma, setSearchedLemma] = useState<string | null>(null);
    const [lookupResults, setLookupResults] = useState<LookupResult[] | null>(null);
    const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
    const [notFound, setNotFound] = useState<boolean>(false);

    const {mutateAsync: lookup, isPending: isLookingUp} = useMutation({
        mutationFn: (lemmaToLookup: string) => postRequest<LookupResult[], LookupRequest>(
            "dictionary/lookup",
            {lemma: lemmaToLookup, language: language}
        ),
    });

    const setLemma = (newLemma: string) => {
        setLemmaState(newLemma);
        setNotFound(false);
    };

    const search = async () => {
        const trimmedLemma = lemma.trim();
        if (!trimmedLemma) {
            return;
        }

        setNotFound(false);

        try {
            const results = await lookup(trimmedLemma);
            setSearchedLemma(trimmedLemma);
            setLookupResults(results);
            setSelectedIndex(results.length > 0 ? 0 : null);
            onLookedUp?.(trimmedLemma);
        } catch (error) {
            if (error instanceof ApiError && error.status === 404 && error.code === "NOT_FOUND") {
                setNotFound(true);
            } else {
                showSnackbar("Failed to look up word.", "error");
            }
        }
    };

    const reset = () => {
        setLemmaState("");
        setSearchedLemma(null);
        setLookupResults(null);
        setSelectedIndex(null);
        setNotFound(false);
    };

    return {
        lemma,
        setLemma,
        searchedLemma,
        lookupResults,
        selectedIndex,
        setSelectedIndex,
        search,
        isLookingUp,
        notFound,
        reset,
    };
};
